import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import type { JsonValue } from '../../lib/mappingEngine';
import { ancestorsOf, childEntries, filterTree, isContainer, joinPath } from '../../lib/jsonTree';

const MAX_SHOWN = 60; // children rendered per node before "… N more"

interface Props {
  doc: JsonValue;
  // Absolute dotted path to mark (the selected field's place in the document).
  highlight?: string;
  // Absolute path of the list holding the records, badged "records".
  recordPath?: string;
  onPick: (path: string, value: JsonValue) => void;
}

const summary = (v: JsonValue): string => (Array.isArray(v) ? `[${v.length}]` : `{${Object.keys(v as object).length}}`);
const leafLabel = (v: JsonValue): string => {
  const s = v === null ? 'null' : typeof v === 'string' ? `"${v}"` : String(v);
  return s.length > 60 ? `${s.slice(0, 60)}…` : s;
};

interface Ctx {
  filtering: boolean;
  visible: Set<string>;
  open: Set<string>;
  highlight?: string;
  recordPath?: string;
  onPick: (path: string, value: JsonValue) => void;
  toggle: (path: string) => void;
  markRef: React.MutableRefObject<HTMLDivElement | null>;
}

function TreeNode({ name, path, value, depth, ctx }: { name: string; path: string; value: JsonValue; depth: number; ctx: Ctx }) {
  if (ctx.filtering && !ctx.visible.has(path)) return null;
  const pad = { paddingLeft: `${depth * 12}px` };
  const isMark = path === ctx.highlight;
  if (!isContainer(value)) {
    return (
      <div ref={isMark ? ctx.markRef : undefined} style={pad}>
        <button
          type="button"
          title={`Use ${path}`}
          onClick={() => ctx.onPick(path, value)}
          className={`flex w-full items-baseline gap-1.5 rounded px-1 py-0.5 text-left text-[12px] font-mono hover:bg-blue-600/10 ${
            isMark ? 'bg-blue-600/20 ring-1 ring-blue-500' : ''
          }`}
        >
          <span className="shrink-0 text-black/60 dark:text-white/60">{name}:</span>
          <span className="truncate text-blue-700 dark:text-blue-300">{leafLabel(value)}</span>
        </button>
      </div>
    );
  }
  const isOpen = ctx.filtering ? ctx.visible.has(path) : ctx.open.has(path);
  const entries = isOpen ? childEntries(value) : [];
  return (
    <div>
      <div style={pad} ref={isMark ? ctx.markRef : undefined}>
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => ctx.toggle(path)}
          className={`flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[12px] font-mono hover:bg-black/[0.05] dark:hover:bg-white/[0.07] ${
            isMark ? 'bg-blue-600/20 ring-1 ring-blue-500' : ''
          }`}
        >
          {isOpen ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
          <span className="font-medium">{name}</span>
          <span className="text-black/40 dark:text-white/40">{summary(value)}</span>
          {ctx.recordPath && path === ctx.recordPath && <span className="rounded bg-emerald-600/15 px-1 text-[10px] text-emerald-700 dark:text-emerald-300">records</span>}
        </button>
      </div>
      {entries.slice(0, MAX_SHOWN).map(([k, c]) => (
        <TreeNode key={k} name={k} path={joinPath(path, k)} value={c} depth={depth + 1} ctx={ctx} />
      ))}
      {entries.length > MAX_SHOWN && (
        <div style={{ paddingLeft: `${(depth + 1) * 12}px` }} className="text-[11px] text-black/40 dark:text-white/40">
          … {entries.length - MAX_SHOWN} more - filter to find them
        </div>
      )}
    </div>
  );
}

// The whole fetched document as a collapsible tree, with a path/value filter on
// top. The selected field's place is highlighted and scrolled to; clicking a
// value picks it. Only open nodes render their children, so a document with
// hundreds of keys stays light.
export function JsonTree({ doc, highlight, recordPath, onPick }: Props) {
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState<Set<string>>(() => new Set(['']));
  const found = useMemo(() => filterTree(doc, filter), [doc, filter]);
  const filtering = filter.trim() !== '';
  const markRef = useRef<HTMLDivElement | null>(null);

  // Selecting a field opens the way to it.
  useEffect(() => {
    if (!highlight) return;
    setOpen((prev) => new Set([...prev, ...ancestorsOf(highlight)]));
  }, [highlight]);
  useEffect(() => {
    markRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [highlight, open]);

  const toggle = (path: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const ctx: Ctx = { filtering, visible: found.visible, open, highlight, recordPath, onPick, toggle, markRef };

  return (
    <div className="flex flex-col gap-1.5">
      <label className="relative block">
        <Search size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-black/40 dark:text-white/40" />
        <input
          aria-label="Filter by path or value"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by path or value (e.g. Revenues)"
          spellCheck={false}
          className="w-full rounded-lg border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 py-1 pl-7 pr-2 text-[12px] outline-none focus:border-blue-500"
        />
      </label>
      {filtering && (
        <span className="text-[11px] text-black/45 dark:text-white/45">
          {found.matches === 0 ? 'No path or value matches.' : `${found.matches}${found.capped ? '+' : ''} match${found.matches === 1 ? '' : 'es'}`}
        </span>
      )}
      <div role="tree" aria-label="Source document" className="max-h-96 overflow-auto rounded-lg border border-black/10 dark:border-white/10 p-1">
        <TreeNode name="(root)" path="" value={doc} depth={0} ctx={ctx} />
      </div>
    </div>
  );
}
